import { useEffect, useState } from 'react';
import { Page } from '../components/Page.js';

type Anchor = {
  id: string; head_seq: number; chain_hash: string; ots_path: string; status: string;
  bitcoin_block_height: number | null; bitcoin_block_time: string | null; submitted_at: string;
};
type Data = { anchors: Anchor[]; latest: Anchor | null; provider: string };

function shortHash(h: string): string { return `${h.slice(0, 10)}…${h.slice(-10)}`; }
function fmt(ts: string): string { return ts.replace('T', ' ').replace(/\.\d+Z$/, ' UTC'); }

export function Verify() {
  const [data, setData] = useState<Data | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    setData(await fetch('/api/anchors').then((r) => r.json()).catch(() => null));
  }
  useEffect(() => { load(); }, []);

  const [msg, setMsg] = useState<string | null>(null);
  async function stamp() { setBusy('stamp'); setMsg(null); try { await fetch('/api/anchors', { method: 'POST' }); await load(); setMsg('New timestamp submitted to the Bitcoin calendars.'); } finally { setBusy(null); } }
  async function upgrade() {
    setBusy('upgrade'); setMsg(null);
    try {
      const r = await fetch('/api/anchors/upgrade', { method: 'POST' }).then((x) => x.json());
      await load();
      setMsg(r.confirmed > 0 ? `${r.confirmed} anchor(s) just confirmed in Bitcoin.` : `Checked ${r.checked ?? 0} pending anchor(s) — none confirmed yet. Bitcoin confirmation usually takes a few hours; this is normal.`);
    } finally { setBusy(null); }
  }

  const latest = data?.latest ?? null;
  const confirmed = latest?.status === 'confirmed';

  return (
    <Page
      eyebrow="Evidence & access"
      title="Verify"
      actions={
        <>
          <button className="btn btn-ghost" type="button" disabled={!!busy} onClick={upgrade}>{busy === 'upgrade' ? 'Checking…' : 'Check for confirmations'}</button>
          <button className="btn btn-primary" type="button" disabled={!!busy} onClick={stamp}>{busy === 'stamp' ? 'Anchoring…' : 'Anchor now'}</button>
        </>
      }
    >
      <section className="crown" style={{ marginBottom: 16 }}>
        <div>
          <div className="crown-eyebrow">The Crown Guarantee — Bitcoin anchoring</div>
          <p>
            The chain head is timestamped into the Bitcoin blockchain via OpenTimestamps. Because the head
            commits to every earlier record, one confirmed anchor proves the whole history existed by that
            block’s time — and <b>no one, not even you, could have produced that proof earlier</b>. That is
            what makes “I reported this on DATE; the police list is merely stale” provable.
          </p>
        </div>
        <div className="crown-head">
          <div className="lab">Latest anchor</div>
          {!latest && <div className="val">none yet</div>}
          {latest && (
            <>
              <div className="val">{confirmed ? `Bitcoin block ${latest.bitcoin_block_height}` : 'Submitted'}</div>
              <div className="sub">
                {confirmed
                  ? (latest.bitcoin_block_time ? fmt(latest.bitcoin_block_time) : 'confirmed')
                  : `pending · submitted ${fmt(latest.submitted_at)}`}
              </div>
            </>
          )}
        </div>
      </section>

      {msg && (
        <div className="card card-pad" style={{ marginBottom: 16, borderLeft: '3px solid var(--accent)', fontSize: 13 }}>{msg}</div>
      )}

      {latest && !confirmed && (
        <div className="card card-pad" style={{ marginBottom: 16, borderLeft: '3px solid var(--warnDot)' }}>
          <strong>Working as intended — your timestamp is in the Bitcoin calendars.</strong>
          <p className="muted" style={{ fontSize: 13, margin: '4px 0 0' }}>
            “Pending” is the normal state for the first few hours. Your chain head has been submitted to
            the public OpenTimestamps calendars (a cryptographic receipt is already held); it becomes a
            full Bitcoin block attestation once the next aggregating transaction is mined and confirmed —
            usually within a few hours. <b>This happens automatically</b> (an hourly check upgrades it); you
            don’t need to do anything. “Check for confirmations” just polls now instead of waiting.
          </p>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="panel-head"><h3>Anchor history</h3><span className="hint">provider: {data?.provider ?? '…'}</span></div>
        <div className="ledger">
          {(!data || data.anchors.length === 0) && <div style={{ padding: 16, color: 'var(--ink3)', fontSize: 13 }}>No anchors yet — click “Anchor now”.</div>}
          {data?.anchors.map((a) => (
            <div className="ledger-row" key={a.id}>
              <span className={`badge badge-${a.status === 'confirmed' ? 'ok' : 'warn'}`}>{a.status.toUpperCase()}</span>
              <div className="ledger-main">
                <div className="ledger-text">
                  head seq {a.head_seq} · <span className="mono muted3">{shortHash(a.chain_hash)}</span>
                  {a.bitcoin_block_height ? <span className="muted"> · block {a.bitcoin_block_height}</span> : null}
                </div>
                <div className="ledger-time">
                  {a.status === 'confirmed' && a.bitcoin_block_time ? `Bitcoin ${fmt(a.bitcoin_block_time)}` : `submitted ${fmt(a.submitted_at)}`}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="panel-foot mono">stamp file = exact hex of chain_hash, no newline · verifiable with the stock `ots` CLI</div>
      </div>

      <div className="card card-pad">
        <h3 style={{ marginTop: 0, fontSize: 14 }}>How anyone can verify this independently</h3>
        <ol className="muted" style={{ fontSize: 13, lineHeight: 1.6, paddingLeft: 18, margin: 0 }}>
          <li><code>pip install opentimestamps-client</code> (gives the <code>ots</code> CLI).</li>
          <li>Put the head’s hex in a file <code>chainhead.txt</code> (no trailing newline) and place the matching <code>chainhead.txt.ots</code> beside it.</li>
          <li><code>ots verify chainhead.txt</code> → prints the Bitcoin block and the UTC time it attests. No trust in this app required.</li>
          <li>Replay the exported ledger segment with SHA-256 to tie any specific record to that head.</li>
        </ol>
      </div>
    </Page>
  );
}
